/**
 * The ground, painted under the lines and the pins on the world map.
 *
 * Under those two, and **over an uploaded drawing** — there is no layer beneath
 * an `<img>`, so this is a wash across the artwork rather than a bed under it,
 * which is why it is semi-transparent and why it defaults to off whenever a
 * picture exists.
 *
 * **A canvas rather than SVG, and that is the whole reason this is not a
 * component like `MapLines`.** The grid is 200x100 — twenty thousand cells — and
 * twenty thousand `<rect>` elements is twenty thousand DOM nodes to draw a
 * picture that never changes between imports. One `putImageData` of the same
 * data is a single element and one blit.
 *
 * Stretched with `image-rendering: pixelated` on purpose. A cell *is* the
 * resolution the engine reads — `terrainCostPerMile` answers per cell — so
 * smoothing the boundaries would draw a precision the rules do not have, and a
 * Owner reading a soft gradient would reasonably expect the ground to change
 * where the colour does.
 *
 * `pointer-events: none`, like the lines above it, because the frame underneath
 * is the Owner's picking button.
 */

import { useEffect, useRef } from 'react';

import { paintTerrain, type TerrainGridView } from './terrain-palette.js';

export function TerrainLayer({
  grid,
  opacity = 0.55,
}: {
  readonly grid: TerrainGridView;
  /**
   * Semi-transparent by default, because this is painted *over* an uploaded
   * drawing: at 1 it is no longer a layer on a map, it is a replacement for one,
   * and it would hide the volcano the picture shows and the coordinates cannot.
   */
  readonly opacity?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    // No 2d context is a browser that cannot draw this at all; the map, the
    // lines and the pins are all still on screen, so there is nothing to
    // announce and nothing to fall back to.
    if (!context) return;

    const { pixels, width, height } = paintTerrain(grid);
    // Written through ImageData rather than a fill per cell: one transfer of the
    // whole raster, and it is the format `paintTerrain` already produces.
    context.putImageData(new ImageData(pixels, width, height), 0, 0);
  }, [grid]);

  return (
    <canvas
      ref={canvasRef}
      // The intrinsic size is the grid's own, and CSS does the stretching — so
      // the raster is transferred at its real resolution and scaled by the
      // compositor rather than being resampled here at whatever width the
      // window happens to be.
      width={grid.width}
      height={grid.height}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{ imageRendering: 'pixelated', opacity }}
    />
  );
}
